import type React from 'react';
import {
  Route,
  Navigate,
  Outlet,
  createRoutesFromElements,
  createBrowserRouter,
} from 'react-router';
import { ApiProvider } from '../apis/sweep/ApisContext';
import Auth0ProviderWithHistory from '../auth/Auth0ProviderWithHistory';
import { AccountSelectionProvider } from './account-selection/AccountSelectionContext';
import {
  appRoutes,
  ROUTING_TAB,
  RoutesThatRequireDefaultCreationEnvConnection,
} from '../constants/appRoutes';
import { lazyLoader } from '../lazyLoader';
import { AppAuthGate } from './AppAuthGate';
import { ChatSubscriptionProvider } from './aiAgentCenter/context/ChatSubscriptionContext';
import { ImpersonationMode } from './ImpersonationMode';
import { Dashboard } from './dashboard/Dashboard';
import AppBootstrap from './pages/AppBootstrap';
import { Version } from './pages/Version';
import { RedirectToDefaultEnv } from './pages/configuration-canvas/RedirectToDefaultEnv';
import { RedirectToAddAccountId } from './pages/configuration-canvas/RedirectToAddAccountId';
import { HubspotOAuth2Redirect } from './pages/environments/HubspotOAuth2Redirect';
import { SalesforceOAuth2Redirect } from './pages/environments/SalesforceOAuth2Redirect';
import { SlackOAuth2Redirect } from './pages/environments/SlackOAuth2Redirect';
import { PanelsProvider } from './panels/PanelsContext';
import { GlobalEnvProvider } from './pages/environments/default-env-context/GlobalEnvProvider';
import noop from 'lodash/noop';
import { TeamsOAuth2Redirect } from './pages/environments/TeamsOAuth2Redirect';
import { McpOAuthRedirect } from './pages/McpOAuthRedirect';
import { CookiesProvider } from 'react-cookie';
import { AccountAutoSwitchEffect } from './account-selection/AccountAutoSwitchEffect';
import GetStartedEntryPoint from './pages/get-started/GetStartedEntryPoint';


const DashboardLayout = () => (
  <>
    <ImpersonationMode />
    <PanelsProvider confirmOpen={noop}>
      <Dashboard>
        <Outlet />
      </Dashboard>
    </PanelsProvider>
  </>
);

const BareLayout = () => (
  <>
    <ImpersonationMode />
    <Outlet />
  </>
);

function lazyRoute(importFn: () => Promise<any>) {
  const Comp = lazyLoader(importFn);
  return <Comp />;
}

// Shared lazy instances — routes that share the same component must share the same
// React.lazy() instance to avoid a Suspense fallback when navigating between them.
const AgentsHomePageView = lazyLoader(() => import('./aiAgentCenter/AgentsHomePageView'));
const ScansListPage = lazyLoader(() => import('./aiAgentCenter/scans/./ScansListPage'));
const ScanDetailsPage = lazyLoader(() => import('./aiAgentCenter/scans/./ScanDetailsPage'));
const ProjectDetailView = lazyLoader(() => import('./pages/projects/ProjectDetailView'));

const AppWithProviders = () => {
  return (
    <CookiesProvider>
      <AccountSelectionProvider>
        <Auth0ProviderWithHistory>
          <AccountAutoSwitchEffect />
          <ApiProvider>
            <ChatSubscriptionProvider>
              <AppAuthGate />
            </ChatSubscriptionProvider>
          </ApiProvider>
        </Auth0ProviderWithHistory>
      </AccountSelectionProvider>
    </CookiesProvider>
  );
};

/**
 * Handles routes that require default creation environment connection.
 * Generate routes that redirect from routes without env to the same routes with env.
 */
const createRedirectToEnvRoutes = () => {
  const redirectToEnvRoutes: React.JSX.Element[] = [];

  RoutesThatRequireDefaultCreationEnvConnection.forEach((routePath) => {
    // Find the corresponding app route configuration
    const appRoute = Object.values(appRoutes).find((route) => route.route === routePath);

    if (!appRoute) {
      console.warn(`No appRoute configuration found for ${routePath}`);
      return;
    }

    // Always include the base route (without env)
    const routePatternsWithoutEnv = [routePath];

    // Add any additional subroutes if defined
    if (appRoute.subroutesForRedirectToEnv) {
      appRoute.subroutesForRedirectToEnv.forEach((subroute) => {
        routePatternsWithoutEnv.push(`${routePath}/${subroute}`);
      });
    }

    // Generate redirect routes for each pattern
    routePatternsWithoutEnv.forEach((patternWithoutEnv, index) => {
      redirectToEnvRoutes.push(
        <Route
          key={`redirect-to-env-${routePath}-${index}`}
          path={patternWithoutEnv}
          element={<RedirectToDefaultEnv />}
        />,
      );
    });

    // Also add redirect routes for URLs with crmOrgId but without accountId
    // e.g., /alerts/:crmOrgId -> /alerts/:crmOrgId/:accountId
    redirectToEnvRoutes.push(
      <Route
        key={`redirect-add-account-${routePath}`}
        path={`${routePath}/:crmOrgId`}
        element={<RedirectToAddAccountId />}
      />,
    );

    // Add redirect routes for subroutes with crmOrgId but without accountId
    if (appRoute.subroutesForRedirectToEnv) {
      appRoute.subroutesForRedirectToEnv.forEach((subroute, index) => {
        redirectToEnvRoutes.push(
          <Route
            key={`redirect-add-account-${routePath}-subroute-${index}`}
            path={`${routePath}/:crmOrgId/${subroute}`}
            element={<RedirectToAddAccountId />}
          />,
        );
      });
    }
  });

  return redirectToEnvRoutes;
};

// Auth0 + createBrowserRouter integration:
// https://github.com/remix-run/react-router/discussions/9321
const routes = (
  <>
    {/* Public routes — outside auth wrapper */}
    <Route
      path="/public/artifacts/:id"
      element={lazyRoute(() => import('./pages/PublicSharedArtifactPage'))}
    />
    <Route path={appRoutes.version.route} element={<Version />} />

    <Route element={<AppWithProviders />}>
      {/* OAuth redirects — inside auth but outside Home initialization */}
      <Route path={appRoutes.salesforce.route} element={<SalesforceOAuth2Redirect />} />
      <Route path={appRoutes.hubspotRedirect.route} element={<HubspotOAuth2Redirect />} />
      <Route path={appRoutes.teamsRedirect.route} element={<TeamsOAuth2Redirect />} />
      <Route path={appRoutes.mcpRedirect.route} element={<McpOAuthRedirect />} />
      <Route path={appRoutes.slack.route} element={<SlackOAuth2Redirect />} />

      <Route
        path={appRoutes.home.route}
        element={
          <GlobalEnvProvider>
            <AppBootstrap />
          </GlobalEnvProvider>
        }
      >
        {/* Feature pages (with dashboard) */}
        <Route element={<DashboardLayout />}>
          <Route
            path={appRoutes.getStarted.route}
            element={<GetStartedEntryPoint />}
          />
          <Route
            path={appRoutes.funnelMapsPage.route}
            element={lazyRoute(() => import('./pages/funnel-maps/FunnelMapsPage'))}
          />
          <Route
            path={appRoutes.devopsCenter.route}
            element={lazyRoute(() => import('./pages/devops-center/DevOpsCenterPage'))}
          />
          <Route
            path={appRoutes.reports.route}
            element={lazyRoute(() => import('./pages/reports/ReportsPage'))}
          />
          <Route
            path={appRoutes.sfChangeFeed.route}
            element={lazyRoute(() => import('./pages/salesforce-logs/SalesforceLogsPage'))}
          />
          <Route
            path={appRoutes.orgMigration.route}
            element={lazyRoute(() => import('./pages/org-migration-demo/OrgMigrationPage'))}
          />
          <Route
            path={`${appRoutes.orgMigration.route}/:tab`}
            element={lazyRoute(() => import('./pages/org-migration-demo/OrgMigrationPage'))}
          />
          <Route
            path={`${appRoutes.rollups.route}/:crmOrgId/:accountId`}
            element={lazyRoute(() => import('./pages/rollups/RollupsPage'))}
          />
          <Route
            path={appRoutes.aiAssistant.route}
            element={lazyRoute(() => import('./pages/ai-assistant/AiAssistantPage'))}
          />
          <Route
            path={`${appRoutes.automations.route}/:crmOrgId/:accountId`}
            element={lazyRoute(() =>
              import('./pages/apps-pages/AutomationPage').then((m) => ({
                default: m.AutomationPage,
              })),
            )}
          />
          <Route
            path={`${appRoutes.documentation.route}/:crmOrgId/:accountId`}
            element={lazyRoute(() =>
              import('./pages/apps-pages/DocumentationPage').then((m) => ({
                default: m.DocumentationPage,
              })),
            )}
          />
          <Route
            path={`${appRoutes.routing.route}/:crmOrgId/:accountId/${ROUTING_TAB}/:routingTab`}
            element={lazyRoute(() =>
              import('./pages/apps-pages/RoutingPageWithProviders').then((m) => ({
                default: m.RoutingPageWithProviders,
              })),
            )}
          />
          <Route
            path={`${appRoutes.routing.route}/:crmOrgId/:accountId`}
            element={lazyRoute(() =>
              import('./pages/apps-pages/RoutingPageWithProviders').then((m) => ({
                default: m.RoutingPageWithProviders,
              })),
            )}
          />
          <Route
            path={`${appRoutes.alerts.route}/:crmOrgId/:accountId`}
            element={lazyRoute(() =>
              import('./pages/apps-pages/AlertsPage').then((m) => ({
                default: m.AlertsPage,
              })),
            )}
          />
          <Route
            path={`${appRoutes.marketingManagement.route}/:crmOrgId/:accountId`}
            element={lazyRoute(() =>
              import('./pages/canvas-pages/canvas-hubspot-page/MarketingAttributionPanel').then(
                (m) => ({ default: m.MarketingAttributionPanel }),
              ),
            )}
          />
          <Route
            path={`${appRoutes.dedup.route}/:crmOrgId/:accountId`}
            element={lazyRoute(() =>
              import('./pages/apps-pages/DedupAndMatchingPage').then((m) => ({
                default: m.DedupAndMatchingPage,
              })),
            )}
          />
          <Route
            path={`${appRoutes.canvasPreviewTest.route}/:funnelMapId/:accountId`}
            element={lazyRoute(
              () => import('./funnel-map-preview-simple/FunnelMapPreviewSimpleTestPage'),
            )}
          />
          <Route
            path={appRoutes.settings.route}
            element={lazyRoute(() => import('./dashboard/user-settings/UserSettings'))}
          />

          {/* AI agents — children render inside AgentsHomePage's own Outlet */}
          <Route
            path={`${appRoutes.agenticLayer.route}/:crmOrgId/:accountId`}
            element={lazyRoute(() => import('./aiAgentCenter/AgentsHomePage'))}
          >
            <Route index element={<AgentsHomePageView />} />
            <Route path="agent/:agentId" element={<AgentsHomePageView />} />
            <Route path="agent/:agentId/chat/:chatId" element={<AgentsHomePageView />} />
            <Route path="scans" element={<ScansListPage />} />
            <Route path="scans/:agentId" element={<ScanDetailsPage />} />
          </Route>
          {/* Projects — detail children render inside ProjectDetailPage's own Outlet */}
          <Route
            path={`${appRoutes.projects.route}/:crmOrgId/:accountId`}
            element={lazyRoute(() => import('./pages/projects/ProjectsListPage'))}
          />
          <Route
            path={`${appRoutes.projects.route}/:crmOrgId/:accountId`}
            element={lazyRoute(() => import('./pages/projects/ProjectDetailPage'))}
          >
            <Route path="agent/:agentId" element={<ProjectDetailView />} />
            <Route path="agent/:agentId/chat/:chatId" element={<ProjectDetailView />} />
          </Route>

          {/* Demos */}
          <Route
            path={appRoutes.demoRuleBuilder.route}
            element={lazyRoute(() =>
              import('./pages/demos/RuleBuilderDemoPage').then((m) => ({
                default: m.RuleBuilderDemoPage,
              })),
            )}
          />
          <Route
            path={appRoutes.demoComponents.route}
            element={lazyRoute(() => import('./pages/demos/ComponentsDemoPage'))}
          />

          {/* Env redirect routes */}
          {createRedirectToEnvRoutes()}
        </Route>

        {/* Pages without dashboard */}
        <Route element={<BareLayout />}>
          <Route
            path={`${appRoutes.funnelEditor.route}/:funnelMapId/:accountId`}
            element={lazyRoute(() =>
              import('./pages/canvas-pages/united-canvas/UnitedConfigurationCanvas').then((m) => ({
                default: m.UnitedDashboard,
              })),
            )}
          />
          <Route
            path={`${appRoutes.funnelEditorReadOnly.route}/:funnelId`}
            element={lazyRoute(() =>
              import('./pages/canvas-pages/united-canvas/read-only/ReadOnlyUnitedConfigurationCanvas').then(
                (m) => ({ default: m.ReadOnlyUnitedDashboard }),
              ),
            )}
          />
          <Route
            path={appRoutes.freeTierOnBoarding.route}
            element={lazyRoute(() => import('./pages/onboarding/SignupEntryPoint'))}
          />
          <Route
            path={appRoutes.questionnaire.route}
            element={lazyRoute(() => import('./pages/questionnaire/QuestionnaireEntryPoint'))}
          />
          <Route
            path={`${appRoutes.shareArtifacts.route}/:versionId`}
            element={lazyRoute(() => import('./pages/SharedArtifactPage'))}
          />
        </Route>

        {/* Account management */}
        <Route
          path={appRoutes.createAccount.route}
          element={lazyRoute(() =>
            import('./account-selection/AccountSelectionCreateAccount').then((m) => ({
              default: m.AccountSelectionCreateAccount,
            })),
          )}
        />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Route>
  </>
);

export const router = createBrowserRouter(createRoutesFromElements(routes));
